import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  MagnifyingGlass, Play, Pause, Download, Trash, SpinnerGap,
  Clock, User, Users, UsersThree, FileText, HardDrives, WarningCircle,
  ArrowUp, Lightning, ShoppingCart,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function formatDuration(sec) {
  if (!sec) return "--:--";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

const MODE_ICONS = { solo: User, duo: Users, panel: UsersThree };

function StorageBar({ used, limit, tier, addonGb, onShowAddons }) {
  if (limit === -1) return null;
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const warning = pct >= 80;
  const full = pct >= 100;

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 space-y-2" data-testid="storage-bar">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HardDrives size={16} className="text-[var(--text-muted)]" />
          <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Media Storage</span>
          {addonGb > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              +{addonGb} GB add-on
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${full ? "text-red-400" : warning ? "text-amber-400" : "text-[var(--text-muted)]"}`}>
            {used.toFixed(1)} / {limit >= 1024 ? `${(limit / 1024).toFixed(1)} GB` : `${limit} MB`}
          </span>
          <button onClick={onShowAddons}
            className="text-[10px] font-medium text-[#FF4400] hover:text-[#FF6633] transition-colors flex items-center gap-1"
            data-testid="storage-add-more-btn">
            <ShoppingCart size={12} /> Add Storage
          </button>
        </div>
      </div>
      <div className="h-2 bg-[var(--bg-base)] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${full ? "bg-red-500" : warning ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {full && (
        <p className="text-[10px] text-red-400 flex items-center gap-1">
          <WarningCircle size={12} />
          {tier === "free_beta"
            ? "Storage full — upgrade to Pro or add storage to keep saving episodes"
            : "Storage full — delete episodes or add more storage"}
        </p>
      )}
      {warning && !full && (
        <p className="text-[10px] text-amber-400 flex items-center gap-1">
          <WarningCircle size={12} /> Storage almost full ({pct.toFixed(0)}% used)
        </p>
      )}
    </div>
  );
}

function StorageAddonsPanel({ api, onClose, onPurchased }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const resp = await api.get("/podcast-storage/addons");
        setData(resp.data);
      } catch (e) { console.error("Failed to load add-ons:", e); }
      setLoading(false);
    };
    fetch();
  }, [api]);

  const handlePurchase = async (type, id) => {
    setPurchasing(id);
    try {
      const endpoint = type === "monthly"
        ? "/stripe/create-podcast-addon-checkout"
        : "/stripe/create-podcast-burst-checkout";
      const body = type === "monthly"
        ? { addon_id: id, origin_url: window.location.origin }
        : { burst_id: id, origin_url: window.location.origin };
      const resp = await api.post(endpoint, body);
      if (resp.data.checkout_url) {
        window.location.href = resp.data.checkout_url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to start checkout";
      toast.error(msg);
    }
    setPurchasing(null);
  };

  if (loading) return <div className="flex justify-center py-6"><SpinnerGap size={20} className="animate-spin text-[var(--text-muted)]" /></div>;
  if (!data) return null;

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--bg-card)] border border-[#FF4400]/20 rounded-xl p-5 space-y-4" data-testid="storage-addons-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <Lightning size={16} className="text-[#FF4400]" /> Expand Your Storage
        </h3>
        <button onClick={onClose} className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]">Close</button>
      </div>

      {/* Monthly add-ons */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-2">
          Monthly Plans ({data.active_addon_count}/{data.max_addons} active)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {(data.monthly_addons || []).map(a => (
            <div key={a.id} className="border border-[var(--border)] rounded-xl p-3 text-center space-y-1.5 hover:border-[#FF4400]/30 transition-colors">
              <p className="text-lg font-bold text-[var(--text-primary)]">+{a.gb} GB</p>
              <p className="text-xs text-[var(--text-muted)]">${(a.price_cents / 100).toFixed(0)}/mo</p>
              <Button onClick={() => handlePurchase("monthly", a.id)}
                disabled={purchasing === a.id || data.active_addon_count >= data.max_addons}
                className="w-full h-8 text-xs bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid={`buy-addon-${a.id}`}>
                {purchasing === a.id ? <SpinnerGap size={12} className="animate-spin" /> : "Subscribe"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Burst packs */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-2">
          One-Time Burst ({data.active_burst_count}/{data.max_bursts} active)
        </p>
        <div className="grid grid-cols-1 gap-2">
          {(data.burst_packs || []).map(b => (
            <div key={b.id} className="border border-[var(--border)] rounded-xl p-3 flex items-center justify-between hover:border-violet-500/30 transition-colors">
              <div>
                <p className="text-sm font-semibold text-[var(--text-primary)]">+{b.gb} GB</p>
                <p className="text-[10px] text-[var(--text-muted)]">${(b.price_cents / 100).toFixed(0)} one-time — expires in {b.duration_days} days</p>
              </div>
              <Button onClick={() => handlePurchase("burst", b.id)}
                disabled={purchasing === b.id || data.active_burst_count >= data.max_bursts}
                className="h-8 text-xs bg-violet-600 hover:bg-violet-500 text-white px-4" data-testid={`buy-burst-${b.id}`}>
                {purchasing === b.id ? <SpinnerGap size={12} className="animate-spin" /> : "Buy Now"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export function StudioPodcastHistory({ api }) {
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [storage, setStorage] = useState(null);
  const [showAddons, setShowAddons] = useState(false);

  // Playback state
  const [playingId, setPlayingId] = useState(null);
  const [audioEl, setAudioEl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);

  // Show notes expand
  const [expandedNotes, setExpandedNotes] = useState(null);

  // Deleting
  const [deletingId, setDeletingId] = useState(null);

  const fetchHistory = useCallback(async (s = skip, q = search) => {
    setLoading(true);
    try {
      const resp = await api.get(`/content-studio/podcast/history?skip=${s}&limit=20&search=${encodeURIComponent(q)}`);
      setProjects(resp.data.projects || []);
      setTotal(resp.data.total || 0);
      setStorage(resp.data.storage || null);
    } catch (e) { console.error("Failed to load podcast history:", e); }
    setLoading(false);
  }, [api, skip, search]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    fetchHistory(0, search);
  };

  const handlePlayAudio = async (projectId) => {
    // Stop current playback
    if (audioEl) { audioEl.pause(); setAudioEl(null); }
    if (playingId === projectId) { setPlayingId(null); return; }

    setLoadingAudio(projectId);
    try {
      const resp = await api.get(`/content-studio/podcast/history/${projectId}/audio`);
      const blob = b64ToBlob(resp.data.audio_base64, resp.data.mime_type || "audio/mpeg");
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { setPlayingId(null); setAudioEl(null); };
      audio.play().catch(() => {});
      setAudioEl(audio);
      setPlayingId(projectId);
    } catch (e) {
      toast.error("Failed to load audio");
    }
    setLoadingAudio(null);
  };

  const handleDownloadAudio = async (project) => {
    setLoadingAudio(project.project_id);
    try {
      const resp = await api.get(`/content-studio/podcast/history/${project.project_id}/audio`);
      const blob = b64ToBlob(resp.data.audio_base64, resp.data.mime_type || "audio/mpeg");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(project.episode_title || "episode").replace(/\s+/g, "-").toLowerCase()}.${project.audio_file_extension || "mp3"}`;
      a.click();
    } catch (e) {
      toast.error("Failed to download audio");
    }
    setLoadingAudio(null);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Delete this podcast episode? This action cannot be undone and will free up storage.")) return;
    setDeletingId(projectId);
    try {
      const resp = await api.delete(`/content-studio/podcast/history/${projectId}`);
      toast.success(`Deleted — reclaimed ${resp.data.reclaimed_mb} MB`);
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
      setTotal(prev => prev - 1);
      // Refresh storage
      fetchHistory(skip, search);
    } catch (e) { toast.error("Failed to delete"); }
    setDeletingId(null);
  };

  const b64ToBlob = (b64, type = "audio/mpeg") => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type });
  };

  return (
    <div className="space-y-4" data-testid="podcast-history">
      {/* Storage bar */}
      {storage && storage.limit_mb !== -1 && (
        <StorageBar used={storage.used_mb} limit={storage.limit_mb} tier={storage.tier}
          addonGb={storage.addon_gb || 0} onShowAddons={() => setShowAddons(!showAddons)} />
      )}

      {/* Storage add-ons panel */}
      <AnimatePresence>
        {showAddons && (
          <StorageAddonsPanel api={api} onClose={() => setShowAddons(false)} onPurchased={() => { setShowAddons(false); fetchHistory(); }} />
        )}
      </AnimatePresence>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search episodes..."
            className="pl-9 bg-[var(--bg-card)] border-[var(--border-strong)] text-sm h-9"
            data-testid="podcast-history-search" />
        </div>
        <Button type="submit" variant="outline" className="h-9 text-xs px-3" data-testid="podcast-history-search-btn">Search</Button>
      </form>

      {/* Episode list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <SpinnerGap size={24} className="animate-spin text-[var(--text-muted)]" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl" data-testid="podcast-history-empty">
          <FileText size={32} className="mx-auto text-[var(--text-muted)] opacity-30 mb-2" />
          <p className="text-sm text-[var(--text-muted)]">{search ? "No episodes match your search" : "No podcast episodes yet"}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map((p, idx) => {
            const ModeIcon = MODE_ICONS[p.speaker_mode] || User;
            const isPlaying = playingId === p.project_id;
            const isLoadingAudio = loadingAudio === p.project_id;
            const isDeleting = deletingId === p.project_id;

            return (
              <motion.div key={p.project_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 hover:border-[var(--border-strong)] transition-colors group"
                data-testid={`podcast-episode-${p.project_id}`}>
                <div className="flex items-start gap-3">
                  {/* Play button or mode icon */}
                  <div className="shrink-0">
                    {p.has_stored_audio ? (
                      <button onClick={() => handlePlayAudio(p.project_id)} disabled={isLoadingAudio}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isPlaying ? "bg-[#FF4400] text-white" : "bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[#FF4400]/10"}`}
                        data-testid={`play-episode-${p.project_id}`}>
                        {isLoadingAudio ? <SpinnerGap size={16} className="animate-spin" /> : isPlaying ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
                      </button>
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-[var(--bg-base)] flex items-center justify-center">
                        <ModeIcon size={16} className="text-[var(--text-muted)]" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{p.episode_title || "Untitled Episode"}</h3>
                      <span className={`px-2 py-0.5 text-[9px] font-medium rounded-full ${
                        p.status === "merged" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : p.status === "audio_ready" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                        : "bg-slate-500/10 text-slate-400 border border-slate-500/20"
                      }`}>{p.status?.replace("_", " ")}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--text-muted)]">
                      <span className="flex items-center gap-1"><ModeIcon size={10} /> {p.speaker_mode}{p.speaker_names?.length > 0 && ` (${p.speaker_names.join(", ")})`}</span>
                      {p.episode_duration_seconds && <span className="flex items-center gap-1"><Clock size={10} /> {formatDuration(p.episode_duration_seconds)}</span>}
                      {p.audio_size_mb > 0 && <span>{p.audio_size_mb} MB</span>}
                      <span>{formatDate(p.created_at)}</span>
                    </div>

                    {/* Show notes toggle */}
                    {p.has_show_notes && (
                      <button onClick={() => setExpandedNotes(expandedNotes === p.project_id ? null : p.project_id)}
                        className="mt-1.5 text-[10px] font-medium text-[#FF4400] hover:text-[#FF6633] transition-colors"
                        data-testid={`toggle-notes-${p.project_id}`}>
                        {expandedNotes === p.project_id ? "Hide show notes" : "View show notes"}
                      </button>
                    )}
                    {!p.has_stored_audio && p.status === "merged" && (
                      <p className="mt-1 text-[10px] text-amber-400 flex items-center gap-1">
                        <ArrowUp size={10} /> Audio not stored — storage was full at time of merge
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {p.has_stored_audio && (
                      <Button onClick={() => handleDownloadAudio(p)} variant="ghost" size="sm" className="h-8 w-8 p-0 text-[var(--text-muted)] hover:text-emerald-400"
                        disabled={isLoadingAudio} data-testid={`download-episode-${p.project_id}`}>
                        <Download size={14} />
                      </Button>
                    )}
                    <Button onClick={() => handleDelete(p.project_id)} variant="ghost" size="sm"
                      className="h-8 w-8 p-0 text-[var(--text-muted)] hover:text-red-400"
                      disabled={isDeleting} data-testid={`delete-episode-${p.project_id}`}>
                      {isDeleting ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} />}
                    </Button>
                  </div>
                </div>

                {/* Expanded show notes */}
                <AnimatePresence>
                  {expandedNotes === p.project_id && p.show_notes && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden">
                      <div className="mt-3 pt-3 border-t border-[var(--border)]">
                        <div className="prose prose-sm prose-invert max-w-none text-[var(--text-secondary)]
                          prose-headings:text-[var(--text-primary)] prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
                          prose-strong:text-[var(--text-primary)] prose-p:my-1 prose-ul:my-1 prose-li:my-0.5"
                          data-testid={`show-notes-${p.project_id}`}>
                          <ReactMarkdown>{p.show_notes}</ReactMarkdown>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-[10px] text-[var(--text-muted)]">
            Showing {skip + 1}-{Math.min(skip + 20, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button onClick={() => { setSkip(Math.max(0, skip - 20)); }} disabled={skip === 0}
              variant="outline" className="text-xs h-8 px-3">Previous</Button>
            <Button onClick={() => { setSkip(skip + 20); }} disabled={skip + 20 >= total}
              variant="outline" className="text-xs h-8 px-3">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
